/**
 * Point symbols on the maps (MB's R143 and R149, 2 Nov 2026): a point feature whose style names a `symbol` is drawn
 * as that shape in its colour instead of a dot. Teslyn Field's and Teslyn Cloud's Leaflet maps build the same SVG
 * from `symbolSvg`; the legends draw the same path. The shapes:
 *
 * - `tower`: a lattice tower, as the Diurnal tool drew the communication towers of a hazard map (`createHazardSvgIcon`
 *   'tower', am:3741): two legs, crossbars and a light on top. The matched (verified) towers are drawn large, the
 *   inferred and unknown ones at the usual size, as the tool did (33 and 22 px).
 * - `fire`: a flame (the Lucide `flame` icon, brand book "Iconography") in the warning's or hot spot's colour, with
 *   a thin edge so it reads on imagery. MB asked for a fire symbol for the state agencies' fires and the satellite hot
 *   spots (R143); the brand book rules out emoji, so the flame is drawn, not typed.
 * - `arrow`: a filled arrowhead pointing the way a patch or reflight is planned to be flown (decision 1011,
 *   `direction-arrows.ts`), turned by the feature's `rotation_deg`, in the line's colour over the lines' dark edge
 *   (`map-line-casing`) rather than the white one, so a yellow reflight arrow still reads on a light map.
 *
 * The colour is data (a token's value, or a hazard file's own colour) and is escaped before it goes into markup.
 */

export type MapSymbol = 'tower' | 'fire' | 'arrow';

/** The edge round a symbol (white on every base map and theme, as the hazard pins' ink): part of the symbol, not of the theme. */
export const SYMBOL_EDGE_INK = '#ffffff';

/** The side of a symbol on the map, px; `large` for the verified towers. */
export const SYMBOL_PX = { normal: 22, large: 33 } as const;

export interface SymbolDrawing {
  readonly viewBox: string;
  /** The filled shape; `fill` takes the colour, the edge is white. */
  readonly fill: readonly string[];
  /** Strokes in the colour over a white halo (the tower's legs and bars). */
  readonly strokes: readonly string[];
}

/** The `arrow` outline in the 24 by 24 box, pointing up (north), centred on (12, 12): tip, right wing, notch, left wing. */
export const ARROW_OUTLINE: ReadonlyArray<readonly [number, number]> = [
  [12, 2.5],
  [20, 20.5],
  [12, 16],
  [4, 20.5],
];

/** The symbols' paths, in a 24 by 24 box. */
export const SYMBOL_DRAWINGS: Readonly<Record<MapSymbol, SymbolDrawing>> = {
  arrow: {
    viewBox: '0 0 24 24',
    fill: [ARROW_OUTLINE.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x} ${y}`).join(' ') + ' Z'],
    strokes: [],
  },
  fire: {
    viewBox: '0 0 24 24',
    fill: [
      'M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z',
    ],
    strokes: [],
  },
  tower: {
    viewBox: '0 0 24 24',
    fill: ['M12 1.5a1.6 1.6 0 1 1 0 3.2 1.6 1.6 0 0 1 0-3.2z'],
    strokes: ['M12 4.5 4.5 23', 'M12 4.5 19.5 23', 'M9.3 10.5h5.4', 'M7.1 16.5h9.8', 'M4.5 23h15'],
  },
};

const escapeAttr = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** A CSS colour safe for an attribute: a hex, `rgb()`/`rgba()`, or a plain colour name; anything else is black. */
export function safeColour(colour: string): string {
  const c = colour.trim();
  return /^(#[0-9a-f]{3,8}|rgba?\([\d.,\s%]+\)|[a-z]+)$/i.test(c) ? c : '#000000';
}

export interface SymbolOptions {
  /** Turned this many degrees clockwise about its centre (an arrow's direction). */
  readonly rotateDeg?: number;
  /** The edge's colour (a CSS colour) instead of white: the lines' dark edge under an arrow. */
  readonly edge?: string;
}

/** The symbol as SVG markup for a Leaflet `divIcon`, `px` wide and high, in `colour` (a CSS colour). */
export function symbolSvg(symbol: MapSymbol, colour: string, px: number, opts: SymbolOptions = {}): string {
  const d = SYMBOL_DRAWINGS[symbol];
  const c = escapeAttr(safeColour(colour));
  const edge = opts.edge ? escapeAttr(safeColour(opts.edge)) : SYMBOL_EDGE_INK;
  const r = opts.rotateDeg;
  const turn = r !== undefined && Number.isFinite(r) && r !== 0 ? Math.round(r * 10) / 10 : null;
  const halo = d.strokes
    .map((p) => `<path d="${p}" fill="none" stroke="${edge}" stroke-width="3.6" stroke-linecap="round"/>`)
    .join('');
  const strokes = d.strokes
    .map((p) => `<path d="${p}" fill="none" stroke="${c}" stroke-width="2" stroke-linecap="round"/>`)
    .join('');
  const fills = d.fill
    .map(
      (p) =>
        `<path d="${p}" fill="${c}" stroke="${edge}" stroke-width="${symbol === 'arrow' ? 1.6 : 1.2}" stroke-linejoin="round"/>`,
    )
    .join('');
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${d.viewBox}" width="${px}" height="${px}" ` +
    `class="map-symbol map-symbol--${symbol}" aria-hidden="true">` +
    (turn === null
      ? `${halo}${strokes}${fills}`
      : `<g transform="rotate(${turn} 12 12)">${halo}${strokes}${fills}</g>`) +
    '</svg>'
  );
}

/** Whether a hazard folder path (e.g. "Communication Towers / Matched Towers (Verified)") holds towers. */
export const isTowerFolder = (path: string): boolean => /tower/i.test(path);

/** The verified (matched) towers are drawn large, as the Diurnal tool drew them. */
export const isLargeTowerFolder = (path: string): boolean =>
  isTowerFolder(path) && /verified|matched/i.test(path);
